import React from 'react';
import './Skeleton.css';

/**
 * Loading placeholders.
 *
 * The product feed arrives over a realtime Firestore listener, so
 * there is always a short window with no data. Showing skeletons
 * shaped like the real cards keeps the page height stable and makes
 * the wait feel shorter than a spinner does.
 */

export function SkeletonBlock({ w = '100%', h = 14, r = 'var(--r-xs)', className = '', style }) {
  return (
    <span
      className={`skel ${className}`}
      style={{ width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

export function SkeletonProductCard() {
  return (
    <div className="skel-card" aria-hidden="true">
      <div className="skel skel-card__media" />
      <div className="skel-card__body">
        <SkeletonBlock w="42%" h={9} />
        <SkeletonBlock w="82%" h={14} />
        <SkeletonBlock w="60%" h={14} />
        <div className="skel-card__foot">
          <SkeletonBlock w="45%" h={18} />
          <SkeletonBlock w="100%" h={34} r="var(--r-sm)" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonProductGrid({ count = 6 }) {
  return (
    <div className="products-grid" role="status" aria-label="Loading products">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonProductCard key={i} />
      ))}
    </div>
  );
}

export function SkeletonCategoryTiles({ count = 5 }) {
  const n = Math.max(1, count);
  return (
    <div
      className="cat-grid"
      role="status"
      aria-label="Loading categories"
      style={{
        '--cat-cols': Math.min(n, 5),
        '--cat-cols-md': Math.min(n, 4),
        '--cat-cols-sm': Math.min(n, 3),
      }}
    >
      {Array.from({ length: n }, (_, i) => (
        <div className="skel-cat" key={i} aria-hidden="true">
          <div className="skel skel-cat__media" />
          <SkeletonBlock w="60%" h={11} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonChips({ count = 5 }) {
  return (
    <div className="skel-chips" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonBlock key={i} w={70 + (i % 3) * 26} h={34} r="var(--r-pill)" />
      ))}
    </div>
  );
}

const Skeleton = {
  Block: SkeletonBlock,
  ProductCard: SkeletonProductCard,
  ProductGrid: SkeletonProductGrid,
  CategoryTiles: SkeletonCategoryTiles,
  Chips: SkeletonChips,
};

export default Skeleton;
